<template>
  <div>
    <div class="text-sm text-center font-semibold text-green-600 flex items-center justify-center gap-1 mb-6">
      <LockClosedIcon class="h-3.5 w-3.5"/>
      <p>Your answer is hidden from companies</p>
      <HoverTooltip tip="It's only used to help us match you with the right job opportunities." placement="left">
        <InformationCircleIcon class="h-4 w-4 text-slate-300 ml-1 -mr-2"/>
      </HoverTooltip>
    </div>
    <div class="mx-auto flex max-w-fit items-center justify-center gap-2 rounded-full border border-slate-200">
      <div
        class="cursor-pointer select-none rounded-full px-14 py-2"
        :class="prefersHourlyOverSalary && 'bg-gradient text-white'"
        tabindex="0"
        @keydown.enter="updatePrefersHourlyOverSalary(true)"
        @click="updatePrefersHourlyOverSalary(true)">
        Hourly
      </div>
      <div
        class="cursor-pointer select-none rounded-full px-14 py-2"
        :class="!prefersHourlyOverSalary && 'bg-gradient text-white'"
        @keydown.enter="updatePrefersHourlyOverSalary(false)"
        @click="updatePrefersHourlyOverSalary(false)">
        Yearly
      </div>
    </div>
    <p class="my-8 text-center text-3xl font-bold">
      {{ pay }}
    </p>
    <FormSliderPay :model-value="desiredHourlyPay" @update:modelValue="updateDesiredHourlyPay" class="mt-8"/>
    <FormLabel label="How flexible are you on compensation?" class="mt-24"/>
    <FormSlider :model-value="compensationFlexibility" @update:modelValue="updateCompensationFlexibility" class="mt-6"
                :min="0.05" :max="0.4" :step="0.01"
                hide-min-max show-output format="percentage"/>
    <FormSliderMessage :buckets="flexibilityBuckets" :value="compensationFlexibility"
                       class="mx-auto mt-10 block text-center"/>
  </div>
</template>

<script setup>
import FormSliderMessage from "./FormSliderMessage.vue";
import FormLabel from "./FormLabel.vue";
import FormSlider from "./FormSlider.vue";
import FormSliderPay from "./FormSliderPay.vue";
import {LockClosedIcon, InformationCircleIcon} from "@heroicons/vue/20/solid/index.js";
import {computed} from "vue";
import HoverTooltip from "./HoverTooltip.vue";

const emits = defineEmits(["update:prefersHourlyOverSalary", "update:desiredHourlyPay", "update:compensationFlexibility"]);
const props = defineProps({
  prefersHourlyOverSalary: Boolean,
  desiredHourlyPay: Number,
  compensationFlexibility: Number,
  includePrivacyNotice: Boolean,
});

const updatePrefersHourlyOverSalary = (value) => emits("update:prefersHourlyOverSalary", value);
const updateDesiredHourlyPay = (value) => emits("update:desiredHourlyPay", value);
const updateCompensationFlexibility = (value) => emits("update:compensationFlexibility", value);

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatterSingle = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatThousandK = (num) => {
  if (num < 1000) {
    return formatter.format(roundByQuarter(num));
  }

  if (num < 1000000) {
    return formatterSingle.format(roundByThousand(num) / 1000) + "K";
  }

  return formatterSingle.format(roundByThousand(num) / 1000000) + "M";
};

function roundByQuarter(value) {
  let inv = 1.0 / 0.25;
  let rounded = Math.round(value * inv) / inv;
  if (rounded < 0) return 0;
  if (rounded > 500) return 500;
  return rounded;
}

function roundByThousand(value) {
  let inv = 1.0 / 1000;
  let rounded = Math.round(value * inv) / inv;
  if (rounded < 0) return 0;
  if (rounded > 1000000) return 1000000;
  return rounded;
}

const pay = computed(() => {
  let amount = formatThousandK(
    props.prefersHourlyOverSalary
      ? props.desiredHourlyPay
      : props.desiredHourlyPay * 2080
  );
  if (props.desiredHourlyPay === 500) amount += "+ ";
  amount += props.prefersHourlyOverSalary ? "/hr" : " /yr";
  return amount;
});

const flexibilityBuckets = [
  {label: "Not Flexible ❌ - You likely will not match with many opportunities", min: 0, max: 0.09},
  {label: "A Little Flexible", min: 0.09, max: 0.16},
  {label: "Somewhat Flexible", min: 0.16, max: 0.25},
  {label: "Very Flexible", min: 0.25, max: 1},
];
</script>
